import config from "../config/config";

const customerService = {
  getAllCustomers: async () => {
    try {
      const response = await fetch(config.baseCustomerUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching customers:", error);
      throw error;
    }
  },

  getCustomerById: async (customerId) => {
    try {
      const response = await fetch(`${config.baseCustomerUrl}/${customerId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching customer with ID ${customerId}:`, error);
      throw error;
    }
  },

  createCustomer: async (customerData, cityAdminData) => {
    // Combine customerData and cityAdminData into one payload
    const payload = {
      ...customerData, // Existing customer data
      cityAdmin: cityAdminData, // Additional city admin data
    };

    // console.log("Payload for creating customer with city admin:", payload);

    try {
      const response = await fetch(config.baseCustomerUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating customer:", error);
      throw error;
    }
  },

  updateCustomer: async (customerId, customerData) => {
    try {
      // console.log("Customer data before update: ", customerData);
      const response = await fetch(`${config.baseCustomerUrl}/${customerId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(customerData),
      });

      if (!response.ok) {
        // Check if the response is JSON
        const contentType = response.headers.get("content-type");
        let errorMessage;
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const errorData = await response.json();
          errorMessage =
            errorData.message || `HTTP error! status: ${response.status}`;
        } else {
          // If not JSON, read the text response
          errorMessage = await response.text();
        }
        throw new Error(errorMessage);
      }

      return response.ok;
    } catch (error) {
      console.error(
        `Error updating customer with ID ${customerId}:`,
        error.message
      );
      throw error;
    }
  },

  deleteCustomer: async (customerId) => {
    try {
      const response = await fetch(`${config.baseCustomerUrl}/${customerId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.ok;
    } catch (error) {
      console.error(`Error deleting customer with ID ${customerId}:`, error);
      throw error;
    }
  },

  checkCustomerNameExists: async (name) => {
    const url = `${config.baseCustomerUrl}/exists?name=${encodeURIComponent(
      name
    )}`;
    try {
      const response = await fetch(url);
      // console.log("response: ", response);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      // Directly return the boolean value from the response
      return await response.json();
    } catch (error) {
      console.error(`Error checking customer name exists:`, error);
      throw error;
    }
  },
};

const placeService = {
  getPlacesByCustomerId: async (customerId) => {
    try {
      const response = await fetch(config.basePlacesUrl(customerId));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching places for customer ${customerId}:`, error);
      throw error;
    }
  },

  getPlaces: async () => {
    try {
      const response = await fetch(`${config.baseCustomerUrl}/places`);
      // console.log("response: ", response);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching places:`, error);
      throw error;
    }
  },
  getPlaceById: async (placeId) => {
    try {
      const response = await fetch(
        `${config.baseCustomerUrl}/places/${placeId}`
      );
      // console.log("response: ", response);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching places:`, error);
      throw error;
    }
  },

  createPlaceForCustomer: async (customerId, placeData) => {
    try {
      // Log the original place data
      // console.log("Original placeData:", placeData);

      // Create a new object that includes customerId in the place data
      const payload = {
        ...placeData, // Spread the original place data
        customerId: customerId, // Add the customerId
      };

      // Log the modified payload for debugging
      // console.log("Payload with customerId:", payload);

      const url = config.basePlacesUrl(customerId);
      // console.log("Request URL:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload), // Send the modified payload
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error creating place for customer ${customerId}:`, error);
      throw error;
    }
  },

  updatePlaceForCustomer: async (customerId, placeId, placeData) => {
    try {
      const response = await fetch(
        `${config.basePlacesUrl(customerId)}/${placeId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(placeData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.ok;
    } catch (error) {
      console.error(
        `Error updating place ${placeId} for customer ${customerId}:`,
        error
      );
      throw error;
    }
  },

  deletePlaceForCustomer: async (customerId, placeId) => {
    try {
      const response = await fetch(
        `${config.basePlacesUrl(customerId)}/${placeId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.ok;
    } catch (error) {
      console.error(
        `Error deleting place ${placeId} for customer ${customerId}:`,
        error
      );
      throw error;
    }
  },
};

// Extend the existing customerService with placeService methods
const extendedCustomerService = {
  ...customerService,
  ...placeService,
};

export default extendedCustomerService;
